import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { History, Download, AlertCircle } from 'lucide-react';
import { api, apiGet } from '../../lib/api.js';
import { money, presetRange, rangeLabel, parseLocalDate } from './billingFormat.js';

const PRESETS = [['day', 'DAY'], ['week', 'WEEK'], ['month', 'MONTH'], ['custom', 'CUSTOM']];

/** "Billing History": payments / usage over a day, week, month or custom window, with CSV export. */
export default function BillingHistory() {
  const [preset, setPreset] = useState('week');
  const [tab, setTab] = useState('payments');
  const [custom, setCustom] = useState({ from: '', to: '' });

  const range = useMemo(() => {
    if (preset !== 'custom') return presetRange(preset);
    const from = parseLocalDate(custom.from);
    const to = parseLocalDate(custom.to, true);
    return from && to ? { from, to } : null;
  }, [preset, custom]);

  const params = range ? { type: tab, from: range.from.toISOString(), to: range.to.toISOString() } : null;
  const { data, isLoading } = useQuery({
    queryKey: ['billing-history', params],
    queryFn: () => apiGet('/billing-center/history', params),
    enabled: !!params,
  });
  const items = data?.items || [];

  async function downloadCsv() {
    try {
      const res = await api.get('/billing-center/history', { params: { ...params, format: 'csv' }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `billing-${tab}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download CSV');
    }
  }

  return (
    <section className="panel p-5" data-testid="billing-history">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><History className="w-5 h-5 text-civix-purpleSoft" /> Billing History</h2>
        <div className="text-right">
          <div className="inline-flex rounded-md overflow-hidden border border-civix-purple/50">
            {PRESETS.map(([id, label]) => (
              <button key={id} type="button" onClick={() => setPreset(id)} className={`px-3 py-1.5 text-xs font-semibold ${preset === id ? 'bg-civix-purple text-white' : 'text-civix-purpleSoft hover:bg-white/5'}`}>{label}</button>
            ))}
          </div>
          {range && <div className="text-[11px] text-white/50 mt-1">Now viewing {rangeLabel(range)}</div>}
        </div>
      </div>

      {preset === 'custom' && (
        <div className="flex gap-3 mt-3">
          <input type="date" aria-label="From date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="input w-44" />
          <input type="date" aria-label="To date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="input w-44" />
        </div>
      )}

      <div className="flex border-b border-white/10 mt-4">
        {[['payments', 'PAYMENTS'], ['usage', 'USAGE']].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} className={`px-6 py-2 text-xs font-semibold border-b-2 ${tab === id ? 'border-civix-purple text-civix-purpleSoft' : 'border-transparent text-white/50'}`}>{label}</button>
        ))}
      </div>

      <div className="flex justify-end mt-3">
        <button type="button" onClick={downloadCsv} disabled={!items.length} className="btn-ghost text-xs font-semibold flex items-center gap-2 disabled:opacity-40">
          DOWNLOAD CSV <Download className="w-3.5 h-3.5" />
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-white/50 py-8 text-center">Loading…</div>
      ) : !range ? (
        <div className="text-sm text-white/50 py-8 text-center">Pick a from and to date.</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-red-400 text-sm flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" /> No {tab} found for the selected time period
        </div>
      ) : (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-white/40 text-left">
              {tab === 'payments'
                ? <tr><th className="py-2">Date</th><th>Type</th><th>Description</th><th className="text-right">Amount</th></tr>
                : <tr><th className="py-2">Date</th><th>Service</th><th>Feature</th><th>Units</th><th className="text-right">Cost</th></tr>}
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-white/5">
                  <td className="py-2 whitespace-nowrap">{new Date(i.date).toLocaleString()}</td>
                  {tab === 'payments' ? (
                    <>
                      <td className="capitalize">{String(i.type).replace(/_/g, ' ')}</td>
                      <td className="text-white/70">{i.description}</td>
                      <td className="text-right">{money(i.amountUsd)}</td>
                    </>
                  ) : (
                    <>
                      <td>{String(i.service).replace(/_/g, ' ')}</td>
                      <td className="text-white/70">{String(i.feature || '').replace(/_/g, ' ')}</td>
                      <td>{i.units} {i.unitType}</td>
                      <td className="text-right">${Number(i.costUsd).toFixed(4)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {data?.truncated && <p className="text-[11px] text-white/40 mt-2">Showing the most recent {items.length} rows — narrow the dates or download the CSV.</p>}
        </div>
      )}
    </section>
  );
}
